// Number formatting for the S3 analytics screens.
//
// The one rule with teeth: a `null` metric is NOT a zero. Every formatter here
// takes `number | null` and returns `NOT_MEASURED` for null, so a panel cannot
// accidentally print "0%" for a column the backfill legitimately left NULL.

import { NOT_MEASURED, PER_VISIT_LABEL } from "./metricLabels";

export function formatCount(value: number | null): string {
  if (value === null) return NOT_MEASURED;
  return value.toLocaleString("en-US");
}

/** One decimal, as the drawn rates are ("2.4"). */
export function formatScore(value: number | null): string {
  if (value === null) return NOT_MEASURED;
  return value.toFixed(1);
}

// ---- THE LOOK-UP RATE: ONE DEFINITION, EVERY CALLER -------------------------
//
// USAGE is `hover_count / unique_sessions` — look-ups per visit that opened the
// term. The overview card (frame 07) and the analytics leaderboard (frame 11)
// draw the same four terms on adjacent screens, and the term detail (frame 13)
// draws one of them again per document. **Two implementations of this would
// drift, and two screens disagreeing about the same term is a credibility bug,
// not a cosmetic one.** So the computation, the ordering and the rendering each
// live here once and every panel imports them.

/** Look-ups per visit, or `null` when no visit opened the term.
 *
 *  `null` is the whole point: a ratio with no denominator has no value, and it
 *  must never collapse to `0` — "nobody opened this" is the opposite claim from
 *  "we could not measure it". `1.0` IS a measured rate, and a good one: every
 *  visit opened the term once and moved on. */
export function lookupsPerSession(
  hovers: number,
  sessions: number,
): number | null {
  return sessions ? hovers / sessions : null;
}

/** Sort comparator, **nulls last**. A rate that could not be computed has no
 *  rank, so it sorts after every measured row rather than being treated as a
 *  zero and ranked below terms that were genuinely opened once per visit. */
export function compareByLookups(a: number | null, b: number | null): number {
  return Number(a === null) - Number(b === null) || (b ?? 0) - (a ?? 0);
}

/** The rate as a reader sees it — "2.4 per visit", or `NOT_MEASURED`.
 *
 *  The qualifier travels with the number everywhere: an unlabelled rate reads
 *  as a raw total, which is the exact misreading the denominator exists to
 *  stop. Use this wherever the unit is not already in a surrounding label. */
export function formatLookupRate(rate: number | null): string {
  if (rate === null) return NOT_MEASURED;
  return `${formatScore(rate)} ${PER_VISIT_LABEL}`;
}

/** A ratio in [0, 1] as a whole-percent string. */
export function formatShare(value: number | null): string {
  if (value === null) return NOT_MEASURED;
  return `${Math.round(value * 100)}%`;
}

/**
 * Visit and dwell lengths. Sub-minute reads as seconds with one decimal
 * ("3.8s"), which is the resolution a dwell time is argued about at; above a
 * minute the decimal is noise, so it steps to "3m 12s" and then "1h 38m".
 *
 * Seeded medians run to ~98 minutes because the seed pairs two unrelated
 * random instants inside one session id (Wave 1 handoff). That is a seed
 * artefact, so no threshold or tone here is tuned to it.
 */
export function formatDuration(ms: number | null): string {
  if (ms === null) return NOT_MEASURED;
  if (ms < 0) return NOT_MEASURED;
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ${Math.round(seconds % 60)}s`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

/** Median of a list, ignoring nulls. Returns null when nothing was measured —
 *  which is different from every value being zero, and stays different.
 *  Deliberately unrounded: the same helper medians milliseconds and ratios in
 *  [0, 1], and rounding would flatten every ratio to 0 or 1. */
export function medianOf(values: Array<number | null>): number | null {
  const present = values.filter((v): v is number => v !== null).sort((a, b) => a - b);
  if (present.length === 0) return null;
  const mid = Math.floor(present.length / 2);
  return present.length % 2 === 1
    ? present[mid]
    : (present[mid - 1] + present[mid]) / 2;
}

/**
 * A signed percentage change, for the Fix-and-Prove ledger.
 *
 * `null` is the backend refusing to answer — too few sessions on one side, or
 * no look-ups before the edit to measure against — and renders `NOT_MEASURED`
 * rather than "0%". The two are opposite claims: 0% says the rewrite changed
 * nothing, `not measured` says we do not know, and a docs manager is about to
 * forward whichever one we print.
 *
 * Whole percents. A decimal place on a behavioural ratio would imply a
 * precision the sample does not have.
 */
export function formatDelta(value: number | null): string {
  if (value === null) return NOT_MEASURED;
  const rounded = Math.round(value);
  // `Math.round(-0.4)` is `-0`, which stringifies as "-0%".
  if (rounded === 0) return "0%";
  return `${rounded > 0 ? "+" : "−"}${Math.abs(rounded)}%`;
}

/** The same number without its sign, for prose that already carries the
 *  direction ("looked up less often, 62%"). Pairing a direction word with a
 *  signed value reads as a double negative — "fell −62%". */
export function formatMagnitude(value: number | null): string {
  if (value === null) return NOT_MEASURED;
  return `${Math.abs(Math.round(value))}%`;
}

/** "8 of 32 documents" — every list on these screens states its total, so a
 *  capped list never reads as a complete one. Takes the SINGULAR noun and
 *  pluralises: a one-document term rendering "1 documents" is the kind of
 *  detail a buyer reads as carelessness about the numbers themselves. */
export function shownOf(shown: number, total: number, singular: string): string {
  const noun = total === 1 ? singular : `${singular}s`;
  return total > shown
    ? `${shown} of ${total.toLocaleString("en-US")} ${noun}`
    : `${total.toLocaleString("en-US")} ${noun}`;
}
